import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { refusalText } from "../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../api/hooks";
import type { CheckResult, ElementRow, HistoryEntry, Id, NoteWithAuthor } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { Button, EmptyState, Pill } from "../../../ui";
import { useRefresh } from "../hooks";
import { contentFacts } from "../lib/facts";
import { elementPath } from "../lib/paths";
import { whenWords } from "../lib/text";
import { FormError, TextArea } from "./fields";
import { NoteItem } from "./marks";

/** A name that links to its element. The name is read once and kept. */
export function ElementLink({ id }: { id: Id }) {
  const query = useApiQuery("element", { id }, { staleTime: 60_000 });
  const row = query.data?.element;
  if (!row) return <span className="muted">{query.isError ? "An element that could not be read" : "…"}</span>;
  return <Link to={elementPath(row)}>{row.name}</Link>;
}

export function ElementLinks({ ids }: { ids: readonly Id[] }) {
  return (
    <>
      {ids.map((id, index) => (
        <span key={id}>
          {index > 0 && ", "}
          <ElementLink id={id} />
        </span>
      ))}
    </>
  );
}

/** The content of an element as a short list of facts in plain words. */
export function ContentFacts({ row }: { row: Pick<ElementRow, "kind" | "content"> }) {
  const facts = contentFacts(row);
  if (facts.length === 0) return null;
  return (
    <dl className="facts">
      {facts.map((fact, index) => (
        <div key={`${fact.label}:${index}`} className="facts__row">
          <dt>{fact.label}</dt>
          <dd>
            {fact.ids && fact.ids.length > 0 && <ElementLinks ids={fact.ids} />}
            {fact.ids && fact.ids.length > 0 && fact.text ? ", " : null}
            {fact.text}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** The notes on an element, with a box to add one. A note changes no state. */
export function NotesPanel({ elementId, notes, canNote }: { elementId: Id; notes: readonly NoteWithAuthor[]; canNote: boolean }) {
  const me = useCurrentMe();
  const roles = me.active?.roles ?? [];
  const add = useApiCommand("add_note");
  const refresh = useRefresh();
  const [text, setText] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!text.trim()) {
      setProblem("Write the note first.");
      return;
    }
    setProblem(null);
    add.mutate(
      { element_id: elementId, kind: roles.includes("engineer") ? "engineer_note" : "sme_note", text: text.trim() },
      {
        onSuccess: () => {
          setText("");
          refresh("element", "back_lane", "inbox", "sme_card");
        },
      },
    );
  };

  return (
    <section aria-labelledby="notes-title">
      <h2 id="notes-title">Notes</h2>
      {notes.length === 0 ? <p className="muted">No notes yet. A note changes no state: the element stays where it is.</p> : (
        <ul className="notes">
          {notes.map((note) => (
            <NoteItem key={note.id} note={note} />
          ))}
        </ul>
      )}
      {canNote && roles.length > 0 && (
        <form onSubmit={submit} className="note-form" noValidate>
          <TextArea label="Add a note" rows={2} value={text} error={problem} onChange={setText} />
          <Button type="submit" busy={add.isPending}>
            Add the note
          </Button>
          <FormError message={add.error ? refusalText(add.error) : null} />
        </form>
      )}
    </section>
  );
}

const SEVERITY_WORDS: Record<CheckResult["severity"], string> = { error: "Error", warning: "Warning", note: "Note" };

/** The checks on one element: errors first, and what blocks a validation. */
export function ChecksPanel({ checks }: { checks: readonly CheckResult[] }) {
  if (checks.length === 0) return <p className="muted">No checks have anything to say about this element.</p>;
  const order = { error: 0, warning: 1, note: 2 } as const;
  const sorted = [...checks].sort((a, b) => order[a.severity] - order[b.severity]);
  return (
    <ul className="check-results">
      {sorted.map((check, index) => (
        <li key={`${check.code ?? "check"}:${index}`} data-severity={check.severity}>
          <Pill tone={check.severity === "error" ? "problem" : "neutral"}>{SEVERITY_WORDS[check.severity]}</Pill> {check.message}
          {check.blocks && <span className="muted"> This blocks a validation.</span>}
        </li>
      ))}
    </ul>
  );
}

/** What happened to an element, newest first. */
export function HistoryPanel({ history }: { history: readonly HistoryEntry[] }) {
  if (history.length === 0) return <EmptyState title="No history yet">Nothing has happened to this element yet.</EmptyState>;
  const sorted = [...history].sort((a, b) => b.event - a.event);
  return (
    <ol className="history">
      {sorted.map((entry) => (
        <li key={entry.event}>
          <span className="history__when muted">{whenWords(entry.at)}</span> <strong>{entry.actor}</strong> {entry.summary}
        </li>
      ))}
    </ol>
  );
}
